import React, { useState, useEffect, type JSX } from 'react';
import { GoogleMap, MarkerClusterer, Marker as GoogleMapMarker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { googleMapStyles } from '../../util/googleMapStyles';
import { DBBoolean, SearchBijlesResponse, SearchBijlesResult } from './SearchBijles.model';

const settings = {
  markerWidth: 25.5,
  markerHeight: 36,
};

const markerStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  width: settings.markerWidth,
  height: settings.markerHeight,
  display: 'block',
  transform: 'translate(-50%, 0)',
  marginTop: settings.markerHeight / -2,
  cursor: 'pointer',
  transition: 'all .3s',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  zIndex: 1,
};

const infoBoxStyle: React.CSSProperties = {
  width: '310px',
  padding: '20px',
  fontSize: '1.6rem',
};

type Filters = SearchBijlesResponse['filters'];
type Results = SearchBijlesResult[];

interface Center {
  lat: number;
  lng: number;
}

interface SearchBijlesMapProps {
  filters: Filters;
  results: Results;
  product: string;
}

export const SearchBijlesMap: React.FC<SearchBijlesMapProps> = ({ filters, results, product }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [center, setCenter] = useState<Center>({ lat: 52.136860, lng: 5.218506 });
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyCSgDlXaQ9Vk_OcQ4cM-sshdGodcK1G41A',
    language: 'nl',
    region: 'NL',
  });

  useEffect(() => {
    const { lng, lat } = filters;
    if (lng !== center.lng || lat !== center.lat) {
      setCenter({
        lat: lat,
        lng: lng,
      });
    }
  }, [filters, center]);

  const toggleOpenIndex = (id_product: string, lat: string, lng: string): void => {
    const verticalMargin = (window.innerWidth > 800) ? 0.0025 : 0.015;
    if (openId === id_product) {
      setOpenId(null);
    } else {
      setOpenId(id_product);
    }
    setCenter({
      lat: parseFloat(lat) + verticalMargin,
      lng: parseFloat(lng),
    });
  };

  const getMapCenter = (): Center => {
    if (center.lat && center.lng) {
      return { lat: center.lat, lng: center.lng };
    }
    if (filters.lat && filters.lng) {
      return { lat: filters.lat, lng: filters.lng };
    }
    return { lat: 52.136860, lng: 5.218506 };
  };

  const getMapZoom = (): number => {
    let distance: number = 0;
    if (results.length > 0) {
      distance = Number(results[0].Distance);
    }
    if (filters.lat && filters.lng) {
      return (window.innerWidth > 800) ? distance < 3 ? 13 : 12 : 12;
    }
    return 8;
  };

  const createMapMarkers = (): JSX.Element[] => {
    return results.map((result: SearchBijlesResult) => (
      <CustomMarker
        key={result.id_product}
        position={{ lat: parseFloat(result.loc_lat), lng: parseFloat(result.loc_long) }}
        isOpen={result.id_product === openId}
        featured={result.timberpost && result.timberpost.is_hub}
        infoBox={{
          location: result.loc_naam,
          id_location: result.id_locatie,
          street: result.loc_straat + ' ' + result.loc_nr,
          zipcode: result.loc_postcode + ' ' + result.loc_stad,
          permalink: result.Permalink,
          product: product,
        }}
        toggleCallback={(
          id: string,
          lat: string,
          lng: string
        ): void => toggleOpenIndex(id, lat, lng)}
        openId={openId}
        setOpenId={setOpenId}
      />
    ));
  };

  const markers = createMapMarkers();
  const style: React.CSSProperties = { height: '60vh', width: '100%' };
  let uniqPlaces = markers.filter((obj, pos: number, arr) => (
    arr.map(mapObj => mapObj.key).indexOf(obj.key) === pos
  ));
  if (window.innerWidth < 992) {
    style.height = '80vh';
  }

  return isLoaded ? <div className='google-map google-map--resizable' style={style}>
    <GoogleMap
      center={getMapCenter()}
      zoom={getMapZoom()}
      options={{
        styles: googleMapStyles,
      }}
    >
      <MarkerClusterer
        options={{
          gridSize: 50,
          maxZoom: 15,
          styles: [
            {
              url: '/wp-content/themes/lyceo-2015/images/marker-cluster.svg',
              width: settings.markerWidth * 1.2,
              height: settings.markerHeight * 1.2,
              textColor: 'white',
              textSize: 15,
              anchorText: [-5, 0],
            },
          ],
        }}
      >
        {(clusterer): JSX.Element => (
          <>
            {uniqPlaces.map((marker) =>
              React.cloneElement(marker, { clusterer: clusterer })
            )}
          </>
        )}
      </MarkerClusterer>

    </GoogleMap>
  </div> : <div>Error loading Google Maps</div>;
};

interface MarkerProps {
  position: { lat: number; lng: number };
  isOpen: boolean;
  featured: DBBoolean;
  infoBox: InfoBoxProps;
  toggleCallback: (id: string, lat: string, lng: string) => void;
}

const CustomMarker: React.FC<MarkerProps & { clusterer?: any; openId: string | null; setOpenId: React.Dispatch<React.SetStateAction<string | null>> }> = ({
  position,
  featured,
  infoBox,
  clusterer,
  openId,
  setOpenId,
}): JSX.Element => {
  const isOpen = openId === infoBox.id_location;
  let style: React.CSSProperties = { ...markerStyle };

  if (isOpen) {
    style.position = 'fixed';
  }

  const handleToggle = (): void => {
    setOpenId(isOpen ? null : infoBox.id_location);
  };

  return (
    <GoogleMapMarker
      position={position}
      onClick={handleToggle}
      icon={{
        url: featured === DBBoolean.TRUE
          ? '/wp-content/themes/lyceo-2015/images/marker-blue.svg'
          : '/wp-content/themes/lyceo-2015/images/marker.svg',
        scaledSize: new window.google.maps.Size(settings.markerWidth, settings.markerHeight),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(settings.markerWidth / 2, settings.markerHeight),
      }}
      clusterer={clusterer}
    >
      {isOpen && (
        <InfoWindow position={position}>
          <InfoBox {...infoBox}/>
        </InfoWindow>
      )}
    </GoogleMapMarker>
  );
};

interface InfoBoxProps {
  location: string;
  id_location: string;
  street: string;
  zipcode: string;
  permalink: string;
  product: string;
}

const InfoBox: React.FC<InfoBoxProps> = ({
  location,
  id_location,
  street,
  zipcode,
  permalink,
  product,
}): JSX.Element => {

  const handleOpenModal = (): void => {
    //@ts-ignore
    $('.contact_aanvragen').modal('show');
    $('.form_loc_name').text(location);
    $('.location_id_field input').val(id_location);
    $(`.location_products_field select option[value="${product}"]`).prop('selected', true);
    $('.location_products_field').hide();
  };

  return (
    <div className='infobox' style={infoBoxStyle}>
      <h4 className={product}>{location}</h4>
      <p>{street}<br/>
        {zipcode}</p>
      <button
        type="button"
        className="list-item__btn btn-default btn btn-block"
        onClick={handleOpenModal}
      >
                Direct contact
      </button>
      <a href={permalink} className="list-item__btn btn btn-gray btn-block">Over deze locatie</a>
    </div>
  );
};
